import { useEffect, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useQuery } from '@apollo/client';
import { gql } from '@apollo/client';
import { SHARED_LINK_QUERY } from '../../graphql/queries';
import { useDocumentFormatsState } from '../../hooks/useDocumentFormats';
import { documentOpensIn } from '../../utils/fileFormats';
import styles from './PDFViewer.module.css';
import Icon from '../../components/common/Icon';

const TOKEN_KEY = 'geodraft_token';

const FILE_QUERY = gql`
  query PDFFile($id: ID!) {
    file(id: $id) {
      id
      originalName
      fileSize
      extension
      status
    }
  }
`;

const formatSize = (bytes: number) => {
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(0)} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
};

// Mounted on two routes: /projects/:projectId/viewer-pdf/:fileId (authenticated)
// and /share/:token/viewer-pdf/:fileId (public share link, no account).
type PDFViewerProps = {
  fileId?: string;
  embedded?: boolean;
};

const PDFViewer = ({ fileId: fileIdProp, embedded = false }: PDFViewerProps) => {
  const { fileId: fileIdParam, token: shareToken } = useParams<{ projectId?: string; fileId: string; token?: string }>();
  const fileId = fileIdProp ?? fileIdParam;
  const isShare = !!shareToken;
  const navigate = useNavigate();

  const [pdfUrl, setPdfUrl] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  const { data, loading: fileLoading } = useQuery(FILE_QUERY, {
    variables: { id: fileId },
    skip: !fileId || isShare,
  });

  // Share visitors have no account — file info comes from the public share-link query.
  const { data: shareData, loading: shareLoading } = useQuery(SHARED_LINK_QUERY, {
    variables: { token: shareToken },
    skip: !isShare,
  });
  const shareLink = shareData?.sharedLink;
  const fileInfo = isShare
    ? shareLink?.files?.find((f: { id: string }) => f.id === fileId)
    : data?.file;

  // A format set to "Download only" (Platform Settings → File Formats) is never
  // fetched for viewing: a view link counts as a document open and spends the
  // account's download GB. Decided once the file and the formats are known; a
  // file whose details could not be read is shown as before.
  const { formats, ready: formatsReady } = useDocumentFormatsState();
  const decided = formatsReady && !(isShare ? shareLoading : fileLoading);
  const downloadOnly = decided && !!fileInfo && documentOpensIn(fileInfo.extension, formats) === 'download';

  // Get a presigned S3 URL and point the iframe at it directly —
  // the PDF streams from S3 to the browser without passing through the backend.
  useEffect(() => {
    if (!fileId || !decided || downloadOnly) return;
    let cancelled = false;

    const load = async () => {
      setLoading(true);
      setError('');
      try {
        const res = isShare
          ? await fetch(`/api/share/${shareToken}/files/${fileId}/view-url`)
          : await fetch(`/api/files/${fileId}/url`, {
              headers: { Authorization: `Bearer ${localStorage.getItem(TOKEN_KEY) || ''}` },
            });
        if (!res.ok) throw new Error(`Server returned ${res.status}`);
        const { url } = await res.json() as { url: string };
        if (!cancelled) setPdfUrl(url);
      } catch (err) {
        if (!cancelled) setError(err instanceof Error ? err.message : 'Failed to load PDF');
      } finally {
        if (!cancelled) setLoading(false);
      }
    };

    load();
    return () => { cancelled = true; };
  }, [fileId, isShare, shareToken, decided, downloadOnly]);

  const handleDownload = async () => {
    if (!fileId || !fileInfo) return;
    if (isShare) {
      // The share download endpoint validates allowDownload and logs the event.
      window.location.href = `/api/share/${shareToken}/files/${fileId}/download`;
      return;
    }
    // Re-fetch a fresh URL with intent=download so the backend records a
    // file_downloaded event (the cached pdfUrl was fetched for viewing).
    const token = localStorage.getItem(TOKEN_KEY) || '';
    let href = pdfUrl;
    try {
      const res = await fetch(`/api/files/${fileId}/url?intent=download`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (res.ok) href = (await res.json() as { url: string }).url;
    } catch { /* fall back to the already-loaded view URL */ }
    if (!href) return;
    const a = document.createElement('a');
    a.href = href;
    a.download = fileInfo.originalName;
    a.click();
  };

  return (
    <div className={`${styles.container} ${embedded ? styles.embedded : ''}`}>
      {/* Header */}
      {!embedded && (
        <header className={styles.header}>
          <div className={styles.headerLeft}>
            <button className={styles.backButton} onClick={() => navigate(-1)}>
              <Icon name="arrow_back" style={{ fontSize: 17 }} />
              Back
            </button>
            <div className={styles.divider} />
            <div className={styles.fileInfo}>
              <Icon name="picture_as_pdf" className={styles.fileIcon} />
              <span className={styles.fileName}>
                {fileInfo?.originalName ?? 'Loading…'}
              </span>
              {fileInfo && (
                <span style={{ fontSize: 11, color: 'rgba(19,27,46,0.35)', flexShrink: 0 }}>
                  {formatSize(fileInfo.fileSize)}
                </span>
              )}
            </div>
          </div>

          <div className={styles.headerRight}>
            {(!isShare || shareLink?.allowDownload) && (
              <button
                className={styles.actionButton}
                onClick={handleDownload}
                disabled={!pdfUrl && !downloadOnly}
              >
                <Icon name="download" style={{ fontSize: 15 }} />
                Download
              </button>
            )}
            {!isShare && (
              <button className={styles.actionButton}>
                <Icon name="share" style={{ fontSize: 15 }} />
                Share
              </button>
            )}
          </div>
        </header>
      )}

      {/* Viewer */}
      <div className={styles.viewerBody}>
        {pdfUrl && (
          <iframe
            className={styles.pdfFrame}
            src={pdfUrl}
            title={fileInfo?.originalName ?? 'PDF Document'}
          />
        )}

        {embedded && !downloadOnly && (!isShare || shareLink?.allowDownload) && (
          <div className={styles.floatingBar}>
            <button
              className={styles.actionButton}
              onClick={handleDownload}
              disabled={!pdfUrl}
            >
              <Icon name="download" style={{ fontSize: 15 }} />
              Download
            </button>
          </div>
        )}

        {downloadOnly && (
          <div className={styles.stateOverlay}>
            <div className={styles.stateIcon}>
              <Icon name="draft" style={{ fontSize: 34, color: '#dc2626' }} />
            </div>
            <p className={styles.stateTitle}>{fileInfo.originalName}</p>
            <p className={styles.stateText}>
              .{(fileInfo.extension || '').toLowerCase()} files have no viewer here. Download the file to open it.
            </p>
            {(!isShare || shareLink?.allowDownload) && (
              <button className={styles.actionButton} onClick={handleDownload}>
                <Icon name="download" style={{ fontSize: 15 }} />
                Download
              </button>
            )}
          </div>
        )}

        {loading && !downloadOnly && (
          <div className={styles.stateOverlay}>
            <div className={styles.stateIcon}>
              <Icon name="picture_as_pdf" style={{ fontSize: 34, color: '#dc2626' }} />
            </div>
            <p className={styles.stateTitle}>Loading PDF…</p>
            <div className={styles.progressBar}>
              <div className={styles.progressFill} />
            </div>
          </div>
        )}

        {error && (
          <div className={styles.stateOverlay}>
            <div className={styles.stateIcon}>
              <Icon name="error" style={{ fontSize: 34, color: '#dc2626' }} />
            </div>
            <p className={styles.stateTitle}>Failed to Load</p>
            <div className={styles.errorBox}>{error}</div>
            <button
              className={styles.actionButton}
              onClick={() => window.location.reload()}
            >
              <Icon name="refresh" style={{ fontSize: 15 }} />
              Retry
            </button>
          </div>
        )}
      </div>
    </div>
  );
};

export default PDFViewer;
